"use client";

import { useStytchB2BClient } from "@stytch/nextjs/b2b";
import { useCallback, useEffect, useRef, useState } from "react";

import { PLATFORM_BASE_URL } from "../../env";
import { reportSignup } from "../../lib/analytics";
import { logError } from "../../logging/logger";
import { useStytchTelemetry } from "../stytch-telemetry-provider";
import { isSignupResult } from "./types";
import { useAuthCompletion } from "./use-auth-completion";

const THIRTY_DAYS = 43200; // 30 days in minutes
const TELEMETRY_TIMEOUT = 45000; // 45 seconds

export const useB2BSignup = () => {
  const stytchB2BClient = useStytchB2BClient();
  const { isReady: stytchTelemetryReady, getTelemetryID } =
    useStytchTelemetry();
  const [isProcessing, setIsProcessing] = useState(false);
  const [telemetryId, setTelemetryId] = useState<string | null>(null);
  const [telemetryError, setTelemetryError] = useState<string | null>(null);
  const [pendingSignup, setPendingSignup] = useState<string | null>(null);
  const { onAuthComplete } = useAuthCompletion();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Effect to fetch telemetry ID when ready
  useEffect(() => {
    if (!stytchTelemetryReady || telemetryId) {
      return;
    }

    const fetchTelemetryId = async () => {
      try {
        const id = await getTelemetryID();
        if (id) {
          setTelemetryId(id);
          // Clear timeout if we got the ID
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        } else {
          throw new Error("Telemetry ID is empty");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to get telemetry ID";
        setTelemetryError(errorMessage);
        logError(error instanceof Error ? error : new Error(String(error)));
      }
    };

    // Set timeout for telemetry ID retrieval
    timeoutRef.current = setTimeout(() => {
      if (!telemetryId) {
        const timeoutError = "Telemetry ID not received within 45 seconds";
        setTelemetryError(timeoutError);
        logError(new Error(timeoutError));
      }
    }, TELEMETRY_TIMEOUT);

    fetchTelemetryId();

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [stytchTelemetryReady, telemetryId, getTelemetryID]);

  const performSignup = useCallback(
    async (intermediateSessionToken: string) => {
      setIsProcessing(true);

      if (!telemetryId) {
        logError(new Error("Telemetry ID still not available"));
        return;
      }

      try {
        const requestBody: { intermediate_session_token?: string } = {};
        if (intermediateSessionToken) {
          requestBody.intermediate_session_token = intermediateSessionToken;
        }

        const reqArgs = {
          method: "POST",
          headers: {
            "content-type": "application/json",
            "x-stytch-telemetry-id": telemetryId,
          },
          body: JSON.stringify(requestBody),
        } as const;

        const response = await fetch(
          `${PLATFORM_BASE_URL}/user/profile/signupb2b`,
          reqArgs,
        );

        const data = await response.json();
        if (!response.ok) {
          if (response.status === 400) {
            if (data.error?.code === "user_already_exists") {
              // expected; continue
            } else {
              throw new Error(
                `Signup failed: ${data.error?.message ?? response.statusText}`,
              );
            }
          } else {
            throw new Error(`Signup failed: ${response.statusText}`);
          }
        }

        if (!isSignupResult(data)) {
          throw new Error("Invalid signup response format");
        }

        const email = stytchB2BClient.self.getSync()?.email_address;
        if (email) {
          reportSignup(email);
        }

        const { session_jwt, session_token } = data;
        stytchB2BClient.session.updateSession({
          session_jwt,
          session_token,
        });
        stytchB2BClient.session.authenticate({
          session_duration_minutes: THIRTY_DAYS,
        });

        await onAuthComplete();
      } catch (error) {
        logError(error instanceof Error ? error : new Error(String(error)));
      } finally {
        setIsProcessing(false);
      }
    },
    [telemetryId, stytchB2BClient, onAuthComplete],
  );

  // Effect to automatically proceed with signup when telemetry ID becomes available
  useEffect(() => {
    if (telemetryId && pendingSignup && !isProcessing && !telemetryError) {
      // Clear the pending signup state and proceed
      const intermediateSessionToken = pendingSignup;
      setPendingSignup(null);

      // Call the actual signup logic
      performSignup(intermediateSessionToken);
    }
  }, [telemetryId, pendingSignup, isProcessing, telemetryError, performSignup]);

  const handleSignup = useCallback(
    async (intermediateSessionToken?: string) => {
      if (isProcessing || !intermediateSessionToken) {
        return;
      }

      // Check if we have telemetry error
      if (telemetryError) {
        logError(new Error(`Cannot proceed with signup: ${telemetryError}`));
        return;
      }

      // If telemetry ID is not available yet, queue the signup
      if (!telemetryId) {
        setPendingSignup(intermediateSessionToken);
        return;
      }

      // If we have telemetry ID, proceed immediately
      await performSignup(intermediateSessionToken);
    },
    [isProcessing, telemetryId, telemetryError, performSignup],
  );

  return { handleSignup, isProcessing } as const;
};
